import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/pages/admin/AdminShell";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, Loader2, Clock, CheckCircle2, XCircle } from "lucide-react";

const STATUS = {
  pending_approval: { label: "Pending", Icon: Clock, color: "bg-[#B58957]/20 text-[#8A4632]" },
  completed: { label: "Approved", Icon: CheckCircle2, color: "bg-[#264C39]/10 text-[#264C39]" },
  denied: { label: "Denied", Icon: XCircle, color: "bg-[#992F2F]/10 text-[#992F2F]" },
  failed: { label: "Failed", Icon: XCircle, color: "bg-[#992F2F]/10 text-[#992F2F]" },
};

export default function AdminWithdrawals() {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [decidingId, setDecidingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/withdrawals?status=${status}&limit=200`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, status]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">Verifying access…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const decide = async (id, decision) => {
    setDecidingId(id);
    try {
      await api.post(`/admin/withdrawals/${id}/decide`, { decision, notes: notes[id] || "" });
      toast.success(`Withdrawal ${decision === "approve" ? "approved" : "denied"}.`);
      setNotes({ ...notes, [id]: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDecidingId(null); }
  };

  return (
    <AdminShell>
      <div className="text-eyebrow text-[#4F4D45]">Compliance · Live withdrawals</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Withdrawal approvals</h1>

      <div className="mt-6">
        <Tabs value={status} onValueChange={setStatus} data-testid="admin-withdraw-tabs">
          <TabsList className="bg-[#E3DECF]">
            <TabsTrigger value="pending_approval" data-testid="admin-withdraw-tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="completed" data-testid="admin-withdraw-tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="denied" data-testid="admin-withdraw-tab-denied">Denied</TabsTrigger>
            <TabsTrigger value="all" data-testid="admin-withdraw-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-6 fv-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#4F4D45]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[#4F4D45]" data-testid="admin-withdraw-empty">No withdrawals in this view.</div>
        ) : (
          <ul className="divide-y divide-[#D2CDBE]" data-testid="admin-withdraw-list">
            {items.map((w) => {
              const meta = STATUS[w.status] || { label: w.status, Icon: Clock, color: "bg-gray-100 text-gray-600" };
              const Icon = meta.Icon;
              return (
                <li key={w.id} className="px-5 py-5" data-testid={`admin-withdraw-item-${w.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display text-xl font-semibold">${(w.amount || 0).toFixed(2)}</div>
                        <span className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${meta.color}`}>
                          <Icon className="w-3.5 h-3.5" /> {meta.label}
                        </span>
                        <span className="text-xs text-[#4F4D45] uppercase tracking-wider">{w.mode}</span>
                      </div>
                      <div className="mt-1 text-sm">
                        <span className="font-medium">{w.user_name}</span>
                        <span className="text-[#4F4D45]"> · {w.user_email} · acct <span className="font-mono">{w.account_number || "—"}</span></span>
                      </div>
                      <div className="text-xs text-[#4F4D45] mt-1">
                        To <strong>{w.destination_bank}</strong> · {w.destination_account} · {w.destination_holder}
                      </div>
                      <div className="text-xs text-[#4F4D45] mt-1">
                        Submitted {w.created_at ? new Date(w.created_at).toLocaleString() : "—"}
                        {w.decided_at && <> · Decided {new Date(w.decided_at).toLocaleString()} by {w.decided_by}</>}
                      </div>
                      {w.decision_notes && <div className="text-xs italic text-[#4F4D45] mt-1">Note: {w.decision_notes}</div>}
                    </div>
                    {w.status === "pending_approval" && (
                      <div className="flex flex-col gap-2 w-full md:w-72">
                        <Textarea
                          value={notes[w.id] || ""}
                          onChange={(e) => setNotes({ ...notes, [w.id]: e.target.value })}
                          placeholder="Decision notes (optional)"
                          className="text-xs h-16"
                          data-testid={`admin-withdraw-notes-${w.id}`}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => decide(w.id, "approve")}
                            disabled={decidingId === w.id}
                            className="flex-1 bg-[#264C39] hover:bg-[#1B3526]"
                            data-testid={`admin-withdraw-approve-${w.id}`}
                          >
                            {decidingId === w.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve</>}
                          </Button>
                          <Button
                            onClick={() => decide(w.id, "deny")}
                            disabled={decidingId === w.id}
                            variant="outline"
                            className="flex-1 border-[#992F2F] text-[#992F2F] hover:bg-[#992F2F] hover:text-white"
                            data-testid={`admin-withdraw-deny-${w.id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Deny
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AdminShell>
  );
}
